import type { ModalOptions } from '@ionic/vue'
import type { LoginDetails } from '~/types/auth'
import type { OnboardingQueries } from '~/types/onboard'

import GBaseModal from '~/components/common/GBaseModal/GBaseModal.vue'
import ModalSignUp from '~/views/login/ModalSignUp.vue'
import ModalSignUpDetails from '~/views/login/ModalSignUpDetails.vue'

export const joinRelishModalId = 'sign-up-modal'

/**
 * @description Query parameters for the onboarding/order-at-table flows
 */
const onboardingQueries = ref<OnboardingQueries | undefined>()
const loginDetails = ref<LoginDetails | undefined>()
/**
 * @description Query parameters for the open from link flows
 */
const linkQueryParams = ref<URLSearchParams | undefined>()

interface ShowSignUpModalOptions {
  queries: OnboardingQueries
  hideBackButton?: boolean
  showMadBundayPromo?: boolean
  queryParams?: URLSearchParams
}

/**
 * @description Handler to display a modal for the user to sign up to Relish.
 */
export function useJoinRelishModal() {
  const { shouldShowMadBundayPromoFromCarousel, resetMadBundayState } = useMadBundayForm()

  const modalOptions: ModalOptions = {
    component: GBaseModal,
    componentProps: {
      id: joinRelishModalId,
      rootPage: ModalSignUp
    }
  }

  /**
   * @description Display the join Relish modal
   * @param queries {OnboardingQueries} The queries to pass during onboarding and order-at-table flows
   * @param showMadBundayPromo {boolean} - Whether the Mad Bunday promo should be shown
   */
  async function showModal({
    queries,
    showMadBundayPromo = false,
    queryParams = undefined
  }: ShowSignUpModalOptions): Promise<void> {
    onboardingQueries.value = queries
    shouldShowMadBundayPromoFromCarousel.value = showMadBundayPromo
    linkQueryParams.value = queryParams

    const modal = await modalController.create({
      ...modalOptions,
      componentProps: {
        modalId: joinRelishModalId,
        ...modalOptions.componentProps
      }
    })

    if (!modal) {
      return
    }

    gaEventUserSignUp(queries.source || 'unknown')

    // Display modal
    return await modal.present()
  }

  /**
   * @description Navigate to the user details form
   * @returns {Promise<boolean>} Whether the navigation was successful
   */
  async function navigateToUserDetailsForm(): Promise<boolean> {
    const signUpModal = document.getElementById(joinRelishModalId) as HTMLIonNavElement
    gaEventJoinRelishSecondPageViewed()

    return await signUpModal.push(ModalSignUpDetails)
  }

  /**
   * @description Navigate back from the coupon details page
   * @returns {Promise<boolean>} Whether the navigation was successful
   */
  function navigateBackToSignUpForm(): Promise<boolean> {
    const signUpModal = document.getElementById(joinRelishModalId) as HTMLIonNavElement

    return signUpModal.pop()
  }

  async function dismissModal() {
    await modalController.dismiss(undefined, undefined, joinRelishModalId)

    onboardingQueries.value = undefined
    loginDetails.value = undefined
    resetMadBundayState()
  }

  return {
    showJoinRelishModal: showModal,
    navigateToUserDetailsForm,
    navigateBackToSignUpForm,
    dismissModal,
    joinRelishModalId,
    shouldShowMadBundayPromoFromCarousel,
    loginDetails,
    queries: onboardingQueries,
    linkQueryParams
  }
}
